import {forwardRef, useState} from "react";
import {capitalize} from "@/utils/index";
import {ArrowLeft, ChevronRight} from "lucide-react";
import {Action, PromptVariable, VariableTypes} from "@/types/node";
import {httpModeColors} from "@/constants/apiMethods";
import {Button} from "@/components/ui/Button";
import {VariableParams} from "@/types/general";

export const PromptDropdown = forwardRef<
  HTMLDivElement,
  {
    selectedType: VariableTypes;
    searchParam: string;
    promptTopPosition: number;
    variableParams: VariableParams;
    setAutocompleteVisible: (visible: boolean) => void;
    onVariableClick: (variable: string, type: VariableTypes) => void;
    getVariablesData: (
      searchParam: string,
      type: VariableTypes,
    ) => PromptVariable[];
  }
>(function PromptDropdown(
  {
    selectedType,
    searchParam,
    promptTopPosition = 0,
    variableParams,
    setAutocompleteVisible,
    onVariableClick,
    getVariablesData,
  },
  ref,
) {
  const [selectedAction, setSelectedAction] = useState<PromptVariable | null>(
    null,
  );

  variableParams[selectedType].content = getVariablesData(
    searchParam,
    selectedType,
  );

  return (
    <div
      ref={ref}
      className={`shadow-md rounded-md bg-white border absolute left-3 right-3 flex flex-col overflow-hidden max-h-80 ${selectedAction ? "min-h-60" : ""}`}
      style={{top: `${promptTopPosition}px`}}
      onMouseDown={e => e.preventDefault()}
    >
      <span className="text-xs text-slate-600 font-medium p-3">
        Add {capitalize(selectedType)}
      </span>

      <div className="flex flex-col gap-1 p-3 pt-0">
        {variableParams[selectedType]?.content.length > 0 ? (
          variableParams[selectedType]?.content.map((item, index) => {
            const variableName =
              selectedType === VariableTypes.VARIABLES
                ? (item as string)
                : (item as Action).formatted_name;

            const httpMode =
              typeof item === "string" ? undefined : (item as Action).http_mode;
            const actionResults =
              typeof item === "string"
                ? undefined
                : (item as Action).action_results;

            const handleClick = () => {
              if (selectedType === VariableTypes.RESULTS) {
                setSelectedAction(item);
              } else {
                onVariableClick(variableName, selectedType);
                setAutocompleteVisible(false);
              }
            };

            return (
              <div
                key={index}
                onClick={() => handleClick()}
                className="text-sm text-primary cursor-pointer flex items-center gap-2 p-1 hover:bg-muted transition-colors duration-100 rounded-md"
              >
                <span className="text-primary-accent tracking-[2px]">
                  {variableParams[selectedType].icon}
                </span>
                {variableName}
                {selectedType === VariableTypes.RESULTS && (
                  <>
                    <div
                      className={`${httpModeColors[httpMode]} text-xs font-bold px-2 py-1 rounded-md`}
                    >
                      {httpMode}
                    </div>
                    {actionResults.length > 0 && (
                      <div className="flex items-center ml-auto text-xs text-slate-500">
                        {actionResults.length} Results{" "}
                        <ChevronRight className="w-4 h-4" />
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })
        ) : (
          <div className="text-sm text-primary font-medium">
            No {capitalize(selectedType)}s Found
          </div>
        )}
      </div>

      {selectedAction && (
        <ActionResults
          action={selectedAction}
          variableParams={variableParams}
          setShowActionResults={() => setSelectedAction(null)}
          onVariableClick={variable => onVariableClick(variable, selectedType)}
          setAutocompleteVisible={setAutocompleteVisible}
        />
      )}
    </div>
  );
});

const ActionResults = ({
  action,
  variableParams,
  setShowActionResults,
  onVariableClick,
  setAutocompleteVisible,
}: {
  action: PromptVariable;
  variableParams: VariableParams;
  setShowActionResults: (show: boolean) => void;
  onVariableClick: (variable: string) => void;
  setAutocompleteVisible: (visible: boolean) => void;
}) => {
  const variableName =
    typeof action === "string" ? action : (action as Action).formatted_name;
  const httpMode =
    typeof action === "string" ? undefined : (action as Action).http_mode;
  const actionResults =
    typeof action === "string" ? undefined : (action as Action).action_results;

  return (
    <div className="flex flex-col bg-white absolute top-0 left-0 w-full h-full">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b">
        <Button
          variant="ghost"
          size="icon-xs"
          className="[&_svg]:size-4"
          onClick={event => {
            event.stopPropagation();
            setShowActionResults(false);
          }}
        >
          <ArrowLeft className="cursor-pointer" />
        </Button>
        <span className="text-sm font-medium">{variableName}</span>
        <div
          className={`${httpModeColors[httpMode]} text-xs font-bold px-2 py-1 rounded-md`}
        >
          {httpMode}
        </div>
      </div>
      <span className="text-xs text-slate-600 font-medium p-3">
        Action Results
      </span>
      <div className="flex flex-col overflow-y-auto p-3 pt-0">
        {actionResults.map((result, index) => (
          <div
            key={index}
            className="px-1 py-1.5 cursor-pointer hover:bg-muted rounded-md transition-colors text-sm"
          >
            <div
              onClick={() => {
                onVariableClick(variableName + ":" + result);
                setAutocompleteVisible(false);
              }}
            >
              <span className="text-primary-accent tracking-[2px] mr-1">
                {variableParams[VariableTypes.ACTIONS].icon}
              </span>
              {result}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
